/**
 * 画像処理ユーティリティ
 * アバター画像の切り抜き、リサイズ、JPEG変換を行う
 */

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 画像を読み込んでHTMLImageElementを返す
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
}

/**
 * 画像を切り抜いてリサイズし、JPEG形式のBase64文字列を返す
 *
 * @param imageSrc - 元画像のData URL
 * @param pixelCrop - 切り抜き領域（ピクセル座標）
 * @param targetSize - リサイズ後のサイズ（正方形）
 * @param quality - JPEG品質（0-1）
 * @returns Base64エンコードされたJPEG画像
 */
export async function createCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  targetSize: number = 200,
  quality: number = 0.85
): Promise<string> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // キャンバスサイズを設定
  canvas.width = targetSize;
  canvas.height = targetSize;

  // 画像を切り抜いてリサイズ
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetSize,
    targetSize
  );

  // JPEG形式でBase64エンコード
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas to Blob conversion failed'));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('FileReader result is not a string'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Base64文字列からデータ部分のみを抽出
 * data:image/jpeg;base64,/9j/4AAQ... → /9j/4AAQ...
 */
export function extractBase64Data(dataUrl: string): string {
  const parts = dataUrl.split(',');
  return parts.length > 1 ? parts[1] : dataUrl;
}

/**
 * ファイルサイズを取得（Base64文字列から）
 * @param base64String - Base64エンコードされた文字列
 * @returns ファイルサイズ（バイト）
 */
export function getBase64FileSize(base64String: string): number {
  const base64Data = extractBase64Data(base64String);
  // Base64は元のサイズの約4/3倍
  return (base64Data.length * 3) / 4;
}

/**
 * ファイルサイズを人間が読みやすい形式に変換
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

/**
 * ファイルをData URLとして読み込む
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not a string'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
